import { useCallback, useEffect, useState } from "react";
import { Gauge, Loader2, MoveRight, X } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";

const ACTIVE = new Set(["queued", "processing"]);
const gb = (b) => `${((b || 0) / 1e9).toFixed(2)} GB`;
const when = (d) => (d ? new Date(d).toLocaleString() : "");
const PREFIX_LABEL = { originals: "Originals", incoming: "Upload chunks", displays: "Display copies", playback: "Video previews", thumbs: "Thumbnails", posters: "Video posters", themes: "Theme backgrounds" };

const Snapshot = ({ s }) => (
  <div className="text-sm" data-testid={`storage-snapshot-${s.label.replace(/\s+/g, "-")}`}>
    <div className="font-medium text-neutral-800">{s.label} · {when(s.at)}</div>
    <div className="text-neutral-600">Total {gb(s.total_bytes)} · keep-set (thumbnails, posters, themes) {gb(s.keep_bytes)}</div>
    <ul className="text-xs text-neutral-500 mt-1 grid grid-cols-2 gap-x-6">
      {Object.entries(s.prefixes || {}).map(([k, v]) => <li key={k}>{PREFIX_LABEL[k] || k}: {v.objects} · {gb(v.bytes)}</li>)}
    </ul>
  </div>
);

const Progress = ({ m, onCancel }) => {
  const c = m.counts || {};
  const total = (m.total?.originals || 0) + (m.total?.previews || 0);
  const done = (c.migrated || 0) + (c.previews || 0) + (c.failed || 0);
  return (
    <div className="mt-4 text-sm text-neutral-700" data-testid="storage-migration-progress">
      <div className="flex items-center gap-3">
        <span data-testid="storage-migration-status">{ACTIVE.has(m.status) ? `Moving… ${done} / ${total}` : `Move ${m.status}${m.finished_at ? ` · ${when(m.finished_at)}` : ""}`}</span>
        {ACTIVE.has(m.status) && <button className="text-xs underline inline-flex items-center gap-1" onClick={onCancel} data-testid="storage-migration-cancel"><X size={12} /> Cancel</button>}
      </div>
      <div className="flex flex-wrap gap-x-5 text-neutral-600 mt-1">
        <span data-testid="storage-migrated">{c.migrated || 0} originals moved</span>
        <span data-testid="storage-previews">{c.previews || 0} video previews moved</span>
        <span data-testid="storage-failed">{c.failed || 0} failed</span>
        <span>{gb(c.bytes)} copied</span>
      </div>
      {m.error && <p className="text-xs text-red-700 mt-1" data-testid="storage-migration-error">{m.error}</p>}
      {(m.failures || []).length > 0 && <ul className="text-xs text-neutral-500 mt-1">{m.failures.slice(-5).map((f, i) => <li key={i}>{f.file}: {f.reason}</li>)}</ul>}
    </div>
  );
};

export const StorageCard = () => {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/admin/storage").then((r) => setS(r.data)), []);
  const running = ACTIVE.has(s?.migration?.status) || s?.measuring;
  useEffect(() => {
    load();
    if (!running) return undefined;
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [load, running]);
  if (!s) return null;
  const call = async (path) => {
    setBusy(true);
    try { setS((await api.post(path)).data); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const p = s.projection;
  const left = s.remaining.originals + s.remaining.previews;
  return (
    <section className="settings-card" data-testid="storage-card">
      <h2 className="text-base font-semibold text-neutral-900">Storage</h2>
      <p className="text-sm text-neutral-500">Originals and video previews live in OneDrive. The site keeps only thumbnails, video posters and theme backgrounds.</p>
      <div className="text-sm text-neutral-700 mt-3 space-y-1">
        <div data-testid="storage-in-drive">{s.in_drive} items stored in OneDrive · {left} still to move{s.pending_offload ? ` · ${s.pending_offload} waiting for OneDrive` : ""}</div>
        <div data-testid="storage-projection">Projected for 50,000 items: {gb(p.projected_50k_bytes)} of the {gb(p.budget_bytes)} budget (thumbnail ≈ {Math.round(p.avg_thumb_bytes / 1000)} KB)</div>
      </div>
      <div className="flex flex-wrap items-center gap-3 mt-4">
        <button className="btn-primary inline-flex items-center gap-2" disabled={busy || running || left === 0} onClick={() => call("/admin/storage/migrate")} data-testid="storage-migrate-button">
          {ACTIVE.has(s.migration?.status) ? <Loader2 size={16} className="animate-spin" /> : <MoveRight size={16} />} {s.migration?.status === "cancelled" || s.migration?.status === "error" ? "Resume move" : "Move originals to OneDrive"}
        </button>
        <button className="text-sm underline inline-flex items-center gap-1" disabled={busy || s.measuring} onClick={() => call("/admin/storage/measure")} data-testid="storage-measure-button">
          {s.measuring ? <Loader2 size={14} className="animate-spin" /> : <Gauge size={14} />} Measure usage
        </button>
      </div>
      {s.migration && <Progress m={s.migration} onCancel={() => call("/admin/storage/migrate/cancel")} />}
      {s.migration?.storage_delete_supported === false && (
        <p className="text-xs text-amber-700 mt-3" data-testid="storage-delete-note">The site's storage can't delete files itself, so moved originals still count until Emergent support clears them.</p>
      )}
      <div className="mt-5 space-y-4" data-testid="storage-snapshots">
        {s.migration?.before && <Snapshot s={s.migration.before} />}
        {s.migration?.after && <Snapshot s={s.migration.after} />}
        {!s.migration?.before && s.snapshots.slice(-1).map((x) => <Snapshot key={x.at} s={x} />)}
      </div>
    </section>
  );
};
